import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Badge, Card, Row, Col } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import Comentarios from "jsx/components/CitoPlus/components/sidebar_comments";
import { LinkPrimary } from "jsx/components/CitoPlus/components/CitoPlusLink";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import noImagen from "images/no-image.jpg";

const URL_NOTAS = "/api/v3/residentes/autorizaciones-notas/";

const AutorizacionDetail = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [autorizacion, setAutorizacion] = useState(null);

  // Comentarios
  const [showSidebarComments, setShowSidebarComments] = useState(false);
  const [urlComentariosParams, setUrlComentariosParams] = useState({});
  const [selectedPrePostData, setSelectedPrePostData] = useState({});

  useEffect(() => {
    fetchAutorizacion();
  }, [slug]);

  const fetchAutorizacion = async () => {
    setLoading(true);
    try {
      const response = await ResidenteService.getAutorizacionDetail(slug);
      setAutorizacion(response.data);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 0:
        return <Badge bg="success">Aprobado</Badge>;
      case 1:
        return <Badge bg="danger">Rechazado</Badge>;
      case 2:
        return <Badge bg="warning">En Proceso</Badge>;
      case 4:
        return <Badge bg="info">Habilitado</Badge>;
      case 5:
        return <Badge bg="secondary">Deshabilitado</Badge>;
      case 6:
        return <Badge bg="primary">Abierto</Badge>;
      case 7:
        return <Badge bg="dark">Cerrado</Badge>;
      default:
        return <Badge bg="secondary">Desconocido</Badge>;
    }
  };

  const handleComentariosClick = () => {
    setSelectedPrePostData({
      autorizacion: slug,
      nota: "",
      foto: null,
    });
    setUrlComentariosParams({
      autorizacion: slug,
      page_size: 100,
    });
    setShowSidebarComments(true);
  };

  const handleNewComment = () => {
    fetchAutorizacion();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <SpinnerCitoPlus text="Cargando autorización..." />
      </div>
    );
  }

  if (!autorizacion) {
    return (
      <div className="text-center py-5">
        <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
        <h5>Autorización no encontrada</h5>
        <LinkPrimary to="/autorizaciones" className="mt-3">
          <i className="fas fa-arrow-left me-2"></i>
          Volver a Autorizaciones
        </LinkPrimary>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">
          Autorización #{autorizacion.consecutivo || "S/N"}
        </h4>
        <LinkPrimary to="/autorizaciones" className="me-2">
          <i className="fas fa-arrow-left me-2"></i>
          Volver
        </LinkPrimary>
        <LinkPrimary
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleComentariosClick();
          }}
        >
          <i className="fas fa-comments me-2"></i>
          Comentarios ({autorizacion.notas?.length || 0})
        </LinkPrimary>
      </div>

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Header>
              <Card.Title>Información de la Autorización</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Asunto</label>
                    <p className="mb-0">{autorizacion.asunto || "-"}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Estado</label>
                    <p className="mb-0">
                      {getEstadoBadge(autorizacion.estado)}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Tipo</label>
                    <p className="mb-0">{autorizacion.tipo_display || "-"}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Inmueble</label>
                    <p className="mb-0">{autorizacion.inmueble || "-"}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Fecha de Creación
                    </label>
                    <p className="mb-0">{autorizacion.created_at || "-"}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Fecha Límite</label>
                    <p className="mb-0">{autorizacion.fecha_limite || "-"}</p>
                  </div>
                </Col>
              </Row>

              {autorizacion.fecha_aprobado && (
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Fecha de Aprobación
                      </label>
                      <p className="mb-0">{autorizacion.fecha_aprobado}</p>
                    </div>
                  </Col>
                </Row>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">Descripción</label>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {autorizacion.nota || "-"}
                </p>
              </div>

              {autorizacion.foto && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Imagen Adjunta</label>
                  <div>
                    <a
                      href={autorizacion.foto}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={autorizacion.foto}
                        alt="Adjunto"
                        className="img-fluid rounded"
                        style={{ maxWidth: "300px" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = noImagen;
                        }}
                      />
                    </a>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {(autorizacion.nombre ||
            autorizacion.cc ||
            autorizacion.documento) && (
            <Card>
              <Card.Header>
                <Card.Title>Datos del Visitante</Card.Title>
              </Card.Header>
              <Card.Body>
                {autorizacion.nombre && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">Nombre</label>
                    <p className="mb-0">{autorizacion.nombre}</p>
                  </div>
                )}
                {autorizacion.cc && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">Documento</label>
                    <p className="mb-0">{autorizacion.cc}</p>
                  </div>
                )}
                {autorizacion.documento && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Archivo de Documento
                    </label>
                    <div>
                      <a
                        href={autorizacion.documento}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="fas fa-file-download me-2"></i>
                        Ver Documento
                      </a>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header>
              <Card.Title>Información Adicional</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <label className="form-label fw-bold">Creado por</label>
                <p className="mb-0">{autorizacion.creado_por_nombre || "-"}</p>
              </div>
              {autorizacion.updated_at && (
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Última Actualización
                  </label>
                  <p className="mb-0">{autorizacion.updated_at}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Comentarios
        showSidebarComments={showSidebarComments}
        setShowSidebarComments={setShowSidebarComments}
        urlComentarios={URL_NOTAS}
        urlComentariosParams={urlComentariosParams}
        urlPost={URL_NOTAS}
        selectedPrePostData={selectedPrePostData}
        handleNewComment={handleNewComment}
      />
    </>
  );
};

export default AutorizacionDetail;
